import { useEffect } from 'react'
import { Link, Outlet, createFileRoute, useMatchRoute, useNavigate, useRouter } from '@tanstack/react-router'
import { z } from 'zod'
import { WAITING_ON, type OrderView } from '@hedgehog/shared'
import { api } from '@/lib/api'
import { AppShell } from '@/components/app/app-shell'
import { ORDER_TABS, OrderError, primaryAction, startSession } from '@/components/app/order'
import { LoadingState } from '@/components/app/states'
import { Badge } from '@/components/ui/badge'
import { buttonVariants } from '@/components/ui/button'
import { useInterval } from '@/hooks/use-interval'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/o/$orderId')({
  validateSearch: z.object({ t: z.string().optional().catch(undefined) }),
  loaderDeps: ({ search }) => ({ t: search.t }),
  loader: async ({ params, deps, abortController }) => {
    if (deps.t) await startSession(params.orderId, deps.t, abortController.signal)
    return api<OrderView>(`/orders/${encodeURIComponent(params.orderId)}`, { signal: abortController.signal })
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: loaderData ? `${loaderData.businessName} · Your order · Hedgehog` : 'Your order · Hedgehog' },
      { name: 'robots', content: 'noindex' },
    ],
  }),
  pendingComponent: () => (
    <AppShell>
      <LoadingState label="Loading your order" />
    </AppShell>
  ),
  errorComponent: ({ error }) => (
    <AppShell>
      <OrderError error={error} title="We could not load your order" />
    </AppShell>
  ),
  component: OrderLayout,
})

function OrderLayout() {
  const order = Route.useLoaderData()
  const { t } = Route.useSearch()
  const navigate = useNavigate()
  const router = useRouter()

  // Drop the access token from the address bar once the session cookie is set.
  useEffect(() => {
    if (t) void navigate({ to: '.', search: (prev) => ({ ...prev, t: undefined }), replace: true })
  }, [t, navigate])

  // Refresh while the system is working, so the page moves on without a reload.
  useInterval(
    () => void router.invalidate({ filter: (m) => m.routeId === Route.id }),
    WAITING_ON[order.state] === 'system' ? 30000 : null,
  )

  const tabs = ORDER_TABS.filter((tab) => tab.available(order))
  const matchRoute = useMatchRoute()
  const next = primaryAction(order)
  // No need to point at the tab the visitor is already on.
  const action = next && 'to' in next && matchRoute({ to: next.to, params: { orderId: order.id } }) ? null : next

  return (
    <AppShell>
      <div className="flex flex-col gap-6">
        <header className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight text-balance sm:text-3xl">{order.businessName}</h1>
            <Badge variant="secondary" className="h-6 px-2.5 text-sm">
              {order.stateLabel}
            </Badge>
          </div>
          <p className="text-muted-foreground">{order.site?.hostname ?? order.domain}</p>
          {(order.nextStep || action) && (
            <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
              {order.nextStep && (
                <p>
                  <span className="font-medium">Next: </span>
                  {order.nextStep}
                </p>
              )}
              {action &&
                ('href' in action ? (
                  <a
                    href={action.href}
                    className={cn(buttonVariants({ size: 'lg' }), 'shrink-0')}
                    {...(action.href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  >
                    {action.label}
                  </a>
                ) : (
                  <Link to={action.to} params={{ orderId: order.id }} className={cn(buttonVariants({ size: 'lg' }), 'shrink-0')}>
                    {action.label}
                  </Link>
                ))}
            </div>
          )}
        </header>

        {tabs.length > 1 && (
          <nav aria-label="Order sections" className="-mx-4 overflow-x-auto border-b border-border px-4">
            <ul className="flex gap-1">
              {tabs.map((tab) => (
                <li key={tab.to}>
                  <Link
                    to={tab.to}
                    params={{ orderId: order.id }}
                    activeOptions={{ exact: tab.to === '/o/$orderId', includeSearch: false }}
                    className="-mb-px inline-flex h-11 items-center border-b-2 border-transparent px-3 text-sm font-medium whitespace-nowrap text-muted-foreground transition-colors outline-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50 data-[status=active]:border-foreground data-[status=active]:text-foreground"
                  >
                    {tab.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        )}

        <Outlet />
      </div>
    </AppShell>
  )
}
